/// <amd-module name="VSS/Features/SecurityUI/RolesDialog/Components/AddRoleAssignmentDialog" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { CustomDialog } from "VSSUI/Dialog";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { TitleSize } from "VSSUI/Header";
import { IdentityPickerDropdown, IPeoplePickerProvider } from "VSSUI/IdentityPicker";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { PanelContent, PanelFooter, PanelHeader } from "VSSUI/Panel";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IItemProvider } from "VSSUI/Utilities/Provider";

import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IdentitiesSearchRequestModel, IIdentity } from "VSS/Features/Identities/IdentityService";
import { SecurityRole } from "VSS/Features/SecurityUI/Contracts/SecurityRoles";
import * as Resources from "VSS/Features/SecurityUI/RolesDialog/Resources";

import { IAddRoleAssignmentDialogProps } from "VSS/Features/SecurityUI/RolesDialog/AddRoleAssignmentDialog.Types";

class RoleDefinitionsProvider implements IItemProvider<IListBoxItem<SecurityRole>> {
    protected roleDefinitions: SecurityRole[];

    constructor(roleDefinitions: SecurityRole[]) {
        this.roleDefinitions = roleDefinitions;
    }

    public get length(): number {
        return this.roleDefinitions.length;
    }

    public get value(): IListBoxItem<SecurityRole>[] {
        return this.roleDefinitions.map(rd => {
            return { data: rd, id: rd.identifier, text: rd.displayName };
        });
    }
}

export class AddRoleAssignmentDialog extends VssComponent<IAddRoleAssignmentDialogProps> {
    private pickerProvider: IPeoplePickerProvider;
    private roleDefinitionsProvider: RoleDefinitionsProvider;
    private roleSelection = new DropdownSelection();
    private selectedIdentity: ObservableValue<IIdentity | undefined>;
    private selectedRole: ObservableValue<SecurityRole>;

    constructor(props: IAddRoleAssignmentDialogProps, context: IVssComponentContext) {
        super(props, context);

        const { identityTypes, model, operationScopes } = this.props;

        this.pickerProvider = new PeoplePickerProvider(this.context.pageContext, {
            autoFocus: true,
            identityTypes,
            operationScopes,
            options: {
                MinResults: 5,
                MaxResults: 20
            }
        } as Partial<IdentitiesSearchRequestModel>);
        this.selectedIdentity = new ObservableValue(undefined);

        const roleDefinitions = model.getRoleDefinitions();
        this.roleDefinitionsProvider = new RoleDefinitionsProvider(roleDefinitions);
        this.selectedRole = new ObservableValue(roleDefinitions[0]);
        this.roleSelection.select(0);
    }

    public render(): JSX.Element {
        const { hideRoleDescriptions, onDismiss } = this.props;
        return (
            <CustomDialog 
                className="add-user-role-dialog" 
                escDismiss={true} 
                onDismiss={onDismiss} 
                ariaLabel={Resources.AddUserDialogTitle} 
                role = {"dialog"}
            > 
                <PanelHeader
                    onDismiss={onDismiss}
                    showCloseButton={false}
                    titleProps={{ text: this.props.addUserDialogUserLabel || Resources.AddUserDialogTitle, size: TitleSize.Small }}
                />
                <PanelContent>
                    <div className="flex-grow">
                        <FormItem className="form-item" label={Resources.AddUserDialogIdentityLabel}>
                            <IdentityPickerDropdown
                                onChange={(identity?: IIdentity) => {
                                    this.selectedIdentity.value = identity;
                                }}
                                pickerProvider={this.pickerProvider}
                                value={undefined}
                            />
                        </FormItem>
                        <FormItem className="form-item" label={Resources.AddUserDialogRoleLabel}>
                            <Dropdown<SecurityRole>
                                items={this.roleDefinitionsProvider}
                                onSelect={(event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<SecurityRole>) => {
                                    this.selectedRole.value = item.data!;
                                }}
                                selection={this.roleSelection}
                            />
                        </FormItem>
                        {!hideRoleDescriptions && (
                            <Observer selectedRole={this.selectedRole}>
                                {(props: { selectedRole: SecurityRole }) => {
                                    return <div className="role-description">{props.selectedRole.description}</div>;
                                }}
                            </Observer>
                        )}
                    </div>
                </PanelContent>
                <Observer identity={this.selectedIdentity} role={this.selectedRole}>
                    {(props: { identity: IIdentity | undefined; role: SecurityRole }) => {
                        const addDisabled = !props.identity || !props.role;
                        return (
                            <PanelFooter
                                buttonProps={[
                                    {
                                        onClick: onDismiss,
                                        text: Resources.AddUserDialogCancelButton
                                    },
                                    {
                                        disabled: addDisabled,
                                        onClick: this.onAddUserClick,
                                        primary: true,
                                        text: Resources.AddUserDialogAddButton
                                    }
                                ]}
                            />
                        );
                    }}
                </Observer>
            </CustomDialog>
        );
    }

    private onAddUserClick = () => {
        const { model, onDismiss } = this.props;
        const identity = this.selectedIdentity.value!;

        // Add user to the role assignment list
        model.addRoleAssignment(this.selectedRole.value, { id: identity.localId!, name: identity.displayName! });

        // Close the dialog
        onDismiss();
    };
}

VssComponent.register("vss-security-ui.add-role-assignment", AddRoleAssignmentDialog);
